import { useState } from 'react';
import { toast } from 'react-toastify';
import { fetchSuiteUsers as fetchSuiteUsersFromAPI } from 'src/services/client';
import { SuiteUserContact, SuiteUserRow } from './types';
import { toSuiteUserRow } from './useSuiteUsersListOperations.helpers';

export interface UseSuiteUsersListOperationsReturn {
  suiteUsers: SuiteUserRow[];
  isFetchingSuiteUsers: boolean;
  fetchSuiteUsers: (suiteId: string) => Promise<void>;
}

/* Read surface for the Suite Users table on the suite page and for KeyHandoff's
 * AddSignee popup (via useKeyPickupData). Replaces the legacy Redux thunk
 * (fetchSuiteUsers + selectSuiteUsers). Delete still routes through the
 * generic deleteContactById thunk in the orchestrator — phase-N follow-up. */
export const useSuiteUsersListOperations =
  (): UseSuiteUsersListOperationsReturn => {
    const [suiteUsers, setSuiteUsers] = useState<SuiteUserRow[]>([]);
    const [isFetchingSuiteUsers, setIsFetchingSuiteUsers] =
      useState<boolean>(false);

    const fetchSuiteUsers = async (suiteId: string): Promise<void> => {
      setIsFetchingSuiteUsers(true);

      try {
        const response: any = await fetchSuiteUsersFromAPI(suiteId);

        if (response.status === 200 || response.ok) {
          const data: SuiteUserContact[] = await response.json();
          const rows: SuiteUserRow[] = data.map(
            (item: SuiteUserContact) => toSuiteUserRow(item)
          );

          setSuiteUsers(rows);
        } else {
          console.error('Failed to fetch suite users:', response.status);
          toast.error(
            '🚫 Failed to load suite users. Please refresh the page.',
            { autoClose: 8000, position: 'top-center' }
          );
        }
      } catch (error) {
        console.error('Error fetching suite users:', error);
        toast.error(
          '🚫 Failed to load suite users. Please refresh the page.',
          { autoClose: 8000, position: 'top-center' }
        );
      } finally {
        setIsFetchingSuiteUsers(false);
      }
    };

    return {
      suiteUsers,
      isFetchingSuiteUsers,
      fetchSuiteUsers,
    };
  };
